import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inventoryAPI, purchaseAPI, suppliersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';
import { MiscReceiptForm } from './components/MiscReceiptForm';
import { CalibrationDialog } from './components/CalibrationDialog';
import { CTSReconcileDialog } from '@/components/CTSReconcileDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const TXN_TYPES = [
  { value: 'receive', label: 'Receive', desc: 'Receive items into stock' },
  { value: 'issue', label: 'Issue', desc: 'Issue items from stock' },
  { value: 'return', label: 'Return', desc: 'Return previously issued items' },
  { value: 'return_to_supplier', label: 'Return to Supplier', desc: 'Return items back to supplier' },
  { value: 'regrind_send', label: 'Send for Regrind', desc: 'Send cutting tools for resharpening' },
  { value: 'regrind_receive', label: 'Receive from Regrind', desc: 'Receive resharpened tools' },
  { value: 'scrap', label: 'Scrap', desc: 'Move items to scrap' },
  { value: 'production_receive', label: 'Receive from Production', desc: 'Finished goods from production' },
];

const TOOL_CONDITIONS = [
  { value: 'good', label: 'Good' },
  { value: 'worn_out_regrind', label: 'Worn Out - Regrinding' },
  { value: 'worn_out_scrap', label: 'Worn Out - Scrap' },
  { value: 'broken', label: 'Broken' },
];

export default function TransactionFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get('type') || 'receive';

  const [items, setItems] = useState([]);
  const [showAllItems, setShowAllItems] = useState(false);
  const [reconcilePromptLine, setReconcilePromptLine] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [godowns, setGodowns] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingWOs, setPendingWOs] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [receiptMode, setReceiptMode] = useState('seeding');
  const [showCalibrationDialog, setShowCalibrationDialog] = useState(false);
  const [calibrationData, setCalibrationData] = useState({
    instrument_name: '', serial_number: '', instrument_code: '',
    range_size: '', least_count: '', frequency_days: '365',
    method: 'External', location: '', acceptance_criteria: '',
    preferred_calibration_agency: '', remark: '',
  });

  const [form, setForm] = useState({
    item_id: '', transaction_type: preselectedType, quantity: '',
    godown_id: '', date: new Date().toISOString().split('T')[0],
    reference_number: '', receiver_id: '', issued_to_id: '',
    remarks: '', tool_unique_id: '', tool_condition: '',
    work_order_id: '', purchase_order_id: '', add_to_calibration: false,
    selected_uom: '', supplier_id: '', supplier_name: '',
    invoice_number: '', lot_number: '', heat_code: '', coc_number: '',
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (form.transaction_type === 'production_receive') loadPendingWOs();
    if (form.transaction_type === 'receive') loadOpenPOs();
    if (form.transaction_type === 'return_to_supplier') loadSuppliers();
  }, [form.transaction_type, form.item_id]);

  const loadData = async () => {
    try {
      const [itemsRes, godownsRes, usersRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        inventoryAPI.listGodowns(),
        inventoryAPI.getInventoryUsers(),
      ]);
      setItems(itemsRes.data.items);
      setGodowns(godownsRes.data);
      setUsers(usersRes.data);
    } catch { toast.error('Failed to load data'); }
  };

  const loadPendingWOs = async () => {
    try {
      const res = await inventoryAPI.getPendingWorkOrders(form.item_id || undefined);
      setPendingWOs(res.data || []);
    } catch { setPendingWOs([]); }
  };

  const loadOpenPOs = async () => {
    try {
      const res = await purchaseAPI.listOpenPOs();
      setOpenPOs(res.data.open_pos || []);
    } catch { setOpenPOs([]); }
  };

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.approvedList({ include_non_as: true });
      setAllSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch { setAllSuppliers([]); }
  };

  const handlePOSelect = (poId) => {
    const po = openPOs.find(p => p.id === poId);
    setSelectedPO(po);
    if (po) {
      setForm(f => ({ ...f, purchase_order_id: poId, reference_number: po.po_number || f.reference_number }));
    } else {
      setSelectedPO(null);
      setForm(f => ({ ...f, purchase_order_id: '' }));
    }
  };

  const handleItemChange = (itemId) => {
    const item = items.find(i => i.id === itemId);
    setSelectedItem(item);
    let godownOverride = {};
    if (form.transaction_type === 'receive') {
      if (item?.perform_qc_on_receipt) {
        const inboundStaging = godowns.find(g => g.code === 'INBOUND_STAGING');
        if (inboundStaging) godownOverride = { godown_id: inboundStaging.id };
      } else {
        const def = godowns.find(g => g.id === item?.default_godown_id)
          || godowns.find(g => g.code === 'STORE');
        if (def) godownOverride = { godown_id: def.id };
      }
    }
    setForm(f => ({ ...f, item_id: itemId, add_to_calibration: false, selected_uom: item?.unit_of_measure || '', ...godownOverride }));
    if (item) {
      setCalibrationData(prev => ({
        ...prev,
        instrument_name: item.item_name || '',
        serial_number: item.unique_id || '',
        instrument_code: item.external_id || '',
      }));
    }
  };

  const handleWOSelect = (woId) => {
    const wo = pendingWOs.find(w => w.id === woId);
    setForm(f => ({ ...f, work_order_id: woId, reference_number: wo?.wo_number || f.reference_number }));
    if (wo?.item_id) handleItemChange(wo.item_id);
  };

  const submitTransaction = async (withCalibration = false) => {
    setLoading(true);
    try {
      const payload = { ...form, quantity: parseFloat(form.quantity) };
      const receiver = users.find(u => u.id === form.receiver_id);
      const issuee = users.find(u => u.id === form.issued_to_id);
      if (receiver) payload.receiver_name = receiver.name;
      if (issuee) payload.issued_to_name = issuee.name;
      if (withCalibration || form.add_to_calibration) {
        payload.calibration_data = { ...calibrationData, frequency_days: parseInt(calibrationData.frequency_days) || 365 };
      }
      const res = await inventoryAPI.createTransaction(payload);
      toast.success(`${form.transaction_type} transaction recorded`);
      if (res.data.work_order_closed) toast.success('Work order marked as completed');
      if (res.data.calibration_tool_id) toast.success('Item added to calibration list');
      // Auto-suggest CTS reconciliation when full sheets were received against a Cut-to-Size PO line.
      if (form.transaction_type === 'receive' && form.purchase_order_id && form.item_id) {
        try {
          const info = await purchaseAPI.ctsReconcileInfo(form.purchase_order_id);
          const line = (info.data?.lines || []).find(l => l.item_id === form.item_id && l.pending_sheets > 0);
          if (line) { setReconcilePromptLine(line); setLoading(false); return; }
        } catch { /* non-blocking */ }
      }
      navigate('/inventory/transactions');
    } catch (err) {
      showApiError(err, 'Failed to create transaction');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.item_id) { toast.error('Please select an item'); return; }
    const selItem = items.find(i => i.id === form.item_id);
    const nonInv = selItem && selItem.is_inventory_item === false;
    if (!nonInv && !form.godown_id) { toast.error('Please select a godown/location'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { toast.error('Quantity must be greater than 0'); return; }
    if (!form.date) { toast.error('Transaction date is required'); return; }
    if (form.transaction_type === 'return_to_supplier' && !form.supplier_id) { toast.error('Please select a supplier'); return; }
    if (form.transaction_type === 'receive') {
      const hasTrace = [form.invoice_number, form.lot_number, form.heat_code, form.coc_number].some(v => v && v.trim());
      if (!hasTrace) { toast.error('At least one traceability field is required (Invoice No., Lot No., Heat Code, or CoC No.)'); return; }
    }
    if (form.purchase_order_id && form.transaction_type === 'receive' && (!form.reference_number || !form.reference_number.trim())) {
      toast.error('Document number (Invoice/Challan No.) is required when receiving against a Purchase Order'); return;
    }
    if (form.add_to_calibration && !showCalibrationDialog) { setShowCalibrationDialog(true); return; }
    await submitTransaction();
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const updateCal = (field, value) => setCalibrationData(prev => ({ ...prev, [field]: value }));

  const isMiscMode = form.transaction_type === 'receive' && receiptMode === 'actual';
  const showCuttingToolFields = selectedItem?.is_cutting_tool && ['receive', 'regrind_receive', 'scrap'].includes(form.transaction_type);
  const isReceiveType = ['receive', 'return', 'regrind_receive', 'production_receive'].includes(form.transaction_type);
  const isIssueType = ['issue', 'regrind_send', 'scrap', 'return_to_supplier'].includes(form.transaction_type);
  const isProductionReceive = form.transaction_type === 'production_receive';
  const isFixedAssetReceive = form.transaction_type === 'receive' && selectedItem?.item_type === 'Fixed Asset';
  const isNonInventory = selectedItem && selectedItem.is_inventory_item === false;
  // Non-inventory (service) items are only selectable for the Receive transaction type
  const visibleItems = items.filter(i => i.is_inventory_item !== false || form.transaction_type === 'receive')
    // Plain Receive is for bought-out material: in-house (make) parts come in via a production
    // receipt. Admins can lift the filter when a manual correction is needed.
    .filter(i => !(form.transaction_type === 'receive' && i.make_or_buy === 'make' && !showAllItems));
  const makeItemsHidden = items.filter(i => i.make_or_buy === 'make').length;

  const woOptions = pendingWOs.map(wo => {
    const traceIds = (wo.traceability_ids || []).filter(Boolean);
    const traceTxt = traceIds.length
      ? `Trace: ${traceIds.slice(0, 3).join(', ')}${traceIds.length > 3 ? ` +${traceIds.length - 3}` : ''}`
      : 'No trace';
    const remaining = Math.max(0, (wo.quantity || 0) - (wo.produced_quantity || 0));
    return {
      value: wo.id,
      label: `${wo.wo_number} - ${wo.item_name}`,
      sub: `${traceTxt} · Remaining: ${remaining}/${wo.quantity} · ${wo.status}`,
      searchStr: `${wo.wo_number} ${wo.item_name} ${wo.customer_name || ''} ${traceIds.join(' ')}`,
    };
  });

  return (
    <div className="space-y-4 max-w-3xl" data-testid="transaction-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="font-heading text-xl font-bold tracking-tight">New Transaction</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Transaction Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Transaction Type & Date */}
            <div>
              <Label className="text-xs">Transaction Type *</Label>
              <Select value={form.transaction_type} onValueChange={v => updateField('transaction_type', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TXN_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div><span className="font-medium">{t.label}</span><span className="text-muted-foreground ml-1">- {t.desc}</span></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={e => updateField('date', e.target.value)} className="h-8 text-xs" data-testid="txn-date" />
            </div>

            {/* Work Order selector for production_receive */}
            {isProductionReceive && (
              <div className="md:col-span-2">
                <Label className="text-xs">Work Order (search by WO# or item name)</Label>
                <SearchableSelect options={woOptions} value={form.work_order_id} onSelect={v => handleWOSelect(v)}
                  placeholder="Search pending work orders..." emptyText="No pending work orders found." testId="txn-wo-select" />

                {/* RM consumption preview — surfaces the trace IDs of the raw material that this WO will consume */}
                {form.work_order_id && (() => {
                  const wo = pendingWOs.find(w => w.id === form.work_order_id);
                  const rmLines = wo?.rm_lines || [];
                  if (!wo) return null;
                  if (rmLines.length === 0) {
                    return (
                      <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="rm-trace-empty">
                        No raw-material trace info recorded for this WO.
                      </p>
                    );
                  }
                  return (
                    <div className="mt-2 border rounded-md bg-amber-50/40 p-2" data-testid="rm-trace-preview">
                      <div className="text-[11px] font-medium mb-1">Raw material that will be consumed for this WO:</div>
                      <table className="w-full text-[10px]">
                        <thead className="text-muted-foreground">
                          <tr>
                            <th className="text-left font-normal pr-2">Trace ID (Cust. WO#)</th>
                            <th className="text-left font-normal pr-2">RM Item</th>
                            <th className="text-left font-normal pr-2">From Inbound DC</th>
                            <th className="text-right font-normal">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rmLines.map((rl, i) => (
                            <tr key={i} className="border-t">
                              <td className="pr-2 py-1">
                                {rl.traceability_id
                                  ? <code className="text-emerald-700">{rl.traceability_id}</code>
                                  : <span className="text-muted-foreground italic">no trace</span>}
                                {rl.traceability_id_description && (
                                  <span className="text-muted-foreground"> · {rl.traceability_id_description}</span>
                                )}
                              </td>
                              <td className="pr-2 py-1">{rl.item_unique_id || rl.item_name}</td>
                              <td className="pr-2 py-1 font-mono text-[9px]">{rl.challan_number}</td>
                              <td className="text-right py-1">{rl.selected_quantity} {rl.unit_of_measure}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-[9.5px] text-muted-foreground mt-1">
                        Consumed FIFO by raw-material trace lot when this receipt is recorded.
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Receipt Mode Toggle */}
            {form.transaction_type === 'receive' && (
              <div className="md:col-span-2">
                <Label className="text-xs mb-1.5 block">Receipt Type</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={receiptMode === 'seeding' ? 'default' : 'outline'}
                    onClick={() => setReceiptMode('seeding')} className="h-8 text-xs" data-testid="receipt-mode-seeding">
                    Seeding / Inventory Entry
                  </Button>
                  <Button type="button" size="sm" variant={receiptMode === 'actual' ? 'default' : 'outline'}
                    onClick={() => setReceiptMode('actual')} className="h-8 text-xs" data-testid="receipt-mode-actual">
                    Actual Receipt (Misc / Non-Inventory)
                  </Button>
                </div>
                {receiptMode === 'actual' && (
                  <p className="text-[10px] text-blue-600 mt-1">
                    For non-inventory items (generic hardware, misc items/services) from NON-AS suppliers. Tracked for spend analytics only, no inventory impact.
                  </p>
                )}
              </div>
            )}

            {/* PO selector for receive seeding mode */}
            {form.transaction_type === 'receive' && receiptMode === 'seeding' && (
              <div className="md:col-span-2">
                <Label className="text-xs">Receive Against PO (optional)</Label>
                <SearchableSelect
                  options={[
                    { value: '_none', label: 'No PO (manual receive)', sub: '', searchStr: 'none manual' },
                    ...openPOs.map(po => ({
                      value: po.id,
                      label: `${po.po_number || po.pr_number} - ${po.supplier_name}`,
                      sub: `${po.items?.length || 0} items pending`,
                      searchStr: `${po.po_number} ${po.pr_number} ${po.supplier_name}`,
                    }))
                  ]}
                  value={form.purchase_order_id || '_none'}
                  onSelect={v => handlePOSelect(v === '_none' ? '' : v)}
                  placeholder="Search by PO# or supplier..." emptyText="No open POs found." testId="txn-po-select"
                />
                {selectedPO && (
                  <div className="mt-2 border rounded p-2 bg-blue-50/50 space-y-1">
                    <p className="text-[10px] font-medium text-blue-700">Pending items from {selectedPO.po_number || selectedPO.pr_number}:</p>
                    {selectedPO.items?.filter(i => i.pending_qty > 0).map((item, idx) => (
                      <button key={idx} type="button"
                        className="w-full text-left text-xs px-2 py-1 rounded hover:bg-blue-100 flex justify-between"
                        onClick={() => { handleItemChange(item.item_id); updateField('quantity', String(item.pending_qty)); }}>
                        <span>{item.unique_id} - {item.item_name}</span>
                        <span className="font-medium text-blue-700">Pending: {item.pending_qty} {item.unit_of_measure}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Supplier selector for return_to_supplier */}
            {form.transaction_type === 'return_to_supplier' && (
              <div className="md:col-span-2">
                <Label className="text-xs">Return to Supplier *</Label>
                <SearchableSelect
                  options={allSuppliers.map(s => ({
                    value: s.id, label: s.supplier_name,
                    sub: `${s.supplier_code || s.supplier_id || ''} ${s.supplier_type === 'NON-AS' ? '(NON-AS)' : ''}`,
                    searchStr: `${s.supplier_name} ${s.supplier_code || ''} ${s.supplier_id || ''}`,
                  }))}
                  value={form.supplier_id}
                  onSelect={v => { const s = allSuppliers.find(sup => sup.id === v); setForm(f => ({ ...f, supplier_id: v, supplier_name: s?.supplier_name || '' })); }}
                  placeholder="Search supplier..." emptyText="No suppliers found." testId="txn-supplier-select"
                />
              </div>
            )}

            {/* Misc Receipt Form OR Normal Inventory Fields */}
            {isMiscMode ? (
              <MiscReceiptForm onSuccess={() => navigate('/inventory/transactions')} onCancel={() => navigate(-1)} loading={loading} setLoading={setLoading} />
            ) : (
            <>
              {/* Item selector */}
              <div>
                <div className="flex items-end justify-between gap-2">
                  <Label className="text-xs">Item *</Label>
                  {form.transaction_type === 'receive' && isAdmin && (
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                      <Checkbox checked={showAllItems} data-testid="receive-show-all-items"
                        onCheckedChange={v => {
                          setShowAllItems(!!v);
                          if (!v && selectedItem?.make_or_buy === 'make') handleItemChange('');
                        }} />
                      Show all items (incl. in-house parts)
                    </label>
                  )}
                </div>
                <SearchableSelect
                  options={visibleItems.map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}${i.is_inventory_item === false ? ' (Service)' : ''}`, sub: `${i.item_type} | ${i.unit_of_measure}`, searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''} ${i.description || ''}` }))}
                  value={form.item_id} onSelect={v => handleItemChange(v)}
                  placeholder="Search by item ID, name..." emptyText="No items found." testId="txn-item-select"
                />
                {form.transaction_type === 'receive' && !showAllItems && makeItemsHidden > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="receive-buy-only-hint">
                    Showing bought-out & customer-supplied items only — {makeItemsHidden} in-house (make)
                    part{makeItemsHidden > 1 ? 's' : ''} hidden. They enter stock from a production receipt.
                  </p>
                )}
              </div>

              {/* Godown — hidden for non-inventory service items (no physical stock) */}
              {isNonInventory ? (
                <div data-testid="txn-godown-na">
                  <Label className="text-xs">Godown</Label>
                  <div className="h-8 text-xs flex items-center px-3 border rounded-md bg-muted text-muted-foreground">
                    Not applicable (service item)
                  </div>
                </div>
              ) : (
              <div>
                <Label className="text-xs">Godown *</Label>
                {selectedItem?.perform_qc_on_receipt && form.transaction_type === 'receive' ? (
                  <div>
                    <div className="h-8 text-xs flex items-center px-3 border rounded-md bg-amber-50 border-amber-200 text-amber-900 font-medium" data-testid="txn-godown-locked">
                      Inbound Staging (QC Required)
                    </div>
                    <p className="text-[10px] text-amber-600 mt-0.5">This item requires QC on receipt. Material will be placed in Inbound Staging for inspection.</p>
                  </div>
                ) : (
                  <SearchableSelect
                    options={godowns.map(g => ({ value: g.id, label: `${g.code} - ${g.name}`, sub: g.description || '', searchStr: `${g.code} ${g.name} ${g.description || ''}` }))}
                    value={form.godown_id} onSelect={v => updateField('godown_id', v)}
                    placeholder="Search godowns..." emptyText="No godowns found." testId="txn-godown-select"
                  />
                )}
                {selectedItem && !selectedItem.perform_qc_on_receipt && form.transaction_type === 'receive'
                  && form.godown_id && (
                  <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="txn-godown-default-hint">
                    Auto-selected from the item's default location — change it if the material goes elsewhere.
                  </p>
                )}
              </div>
              )}

              {/* Quantity */}
              <div>
                <Label className="text-xs">Quantity *</Label>
                <Input type="number" step="0.01" value={form.quantity} onChange={e => updateField('quantity', e.target.value)}
                  placeholder="0" className="h-8 text-xs" data-testid="txn-quantity" />
                {selectedItem?.secondary_uom && selectedItem?.conversion_factor > 0 && form.selected_uom && form.selected_uom !== selectedItem.unit_of_measure && form.quantity && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">= {(parseFloat(form.quantity) / selectedItem.conversion_factor).toFixed(4)} {selectedItem.unit_of_measure} (primary)</p>
                )}
                {selectedItem?.secondary_uom && selectedItem?.conversion_factor > 0 && (!form.selected_uom || form.selected_uom === selectedItem.unit_of_measure) && form.quantity && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">= {(parseFloat(form.quantity) * selectedItem.conversion_factor).toFixed(4)} {selectedItem.secondary_uom} (secondary)</p>
                )}
              </div>

              {/* UOM selector */}
              {selectedItem?.secondary_uom && (
                <div>
                  <Label className="text-xs">UOM</Label>
                  <Select value={form.selected_uom || selectedItem.unit_of_measure} onValueChange={v => updateField('selected_uom', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="txn-uom-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={selectedItem.unit_of_measure}>{selectedItem.unit_of_measure} (Primary)</SelectItem>
                      <SelectItem value={selectedItem.secondary_uom}>{selectedItem.secondary_uom} (Secondary)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Reference Number */}
              <div>
                <Label className="text-xs">
                  {form.purchase_order_id && isReceiveType ? 'Document No. (Invoice/Challan) *' : 'Reference Number'}
                </Label>
                <Input value={form.reference_number} onChange={e => updateField('reference_number', e.target.value)}
                  placeholder={form.purchase_order_id && isReceiveType ? 'Invoice / Challan number (required)' : 'PO/WO/Challan number'}
                  className={`h-8 text-xs ${form.purchase_order_id && isReceiveType && !form.reference_number ? 'border-amber-400' : ''}`}
                  data-testid="txn-reference" />
              </div>

              {/* Received By */}
              {isReceiveType && (
                <div>
                  <Label className="text-xs">Received By (User)</Label>
                  <SearchableSelect
                    options={users.map(u => ({ value: u.id, label: u.name, sub: u.email, searchStr: `${u.name} ${u.email}` }))}
                    value={form.receiver_id} onSelect={v => updateField('receiver_id', v)}
                    placeholder="Search users..." emptyText="No users with inventory access." testId="txn-receiver-select"
                  />
                </div>
              )}

              {/* Traceability Fields */}
              {isReceiveType && (
                <div className="col-span-2 border border-amber-300 bg-amber-50/30 rounded-md p-3 space-y-2">
                  <p className="text-xs font-medium text-amber-800">Traceability Info * (at least one field required)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Invoice Number</Label>
                      <Input value={form.invoice_number} onChange={e => updateField('invoice_number', e.target.value)}
                        placeholder="INV-12345" className="h-7 text-xs" data-testid="txn-invoice-number" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Lot Number</Label>
                      <Input value={form.lot_number} onChange={e => updateField('lot_number', e.target.value)}
                        placeholder="LOT-001" className="h-7 text-xs" data-testid="txn-lot-number" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Heat Code</Label>
                      <Input value={form.heat_code} onChange={e => updateField('heat_code', e.target.value)}
                        placeholder="HC-2026-A" className="h-7 text-xs" data-testid="txn-heat-code" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">CoC Number</Label>
                      <Input value={form.coc_number} onChange={e => updateField('coc_number', e.target.value)}
                        placeholder="CoC-001" className="h-7 text-xs" data-testid="txn-coc-number" />
                    </div>
                  </div>
                </div>
              )}

              {/* Issued To */}
              {isIssueType && (
                <div>
                  <Label className="text-xs">Issued To (User)</Label>
                  <SearchableSelect
                    options={users.map(u => ({ value: u.id, label: u.name, sub: u.email, searchStr: `${u.name} ${u.email}` }))}
                    value={form.issued_to_id} onSelect={v => updateField('issued_to_id', v)}
                    placeholder="Search users..." emptyText="No users with inventory access." testId="txn-issuedto-select"
                  />
                </div>
              )}
            </>)}
          </CardContent>
        </Card>

        {/* Cutting Tool Fields */}
        {!isMiscMode && showCuttingToolFields && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Cutting Tool Tracking</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tool Unique ID</Label>
                <Input value={form.tool_unique_id} onChange={e => updateField('tool_unique_id', e.target.value)}
                  placeholder="Individual tool ID" className="h-8 text-xs font-mono" data-testid="txn-tool-id" />
              </div>
              <div>
                <Label className="text-xs">Tool Condition</Label>
                <Select value={form.tool_condition || 'none'} onValueChange={v => updateField('tool_condition', v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="txn-tool-condition"><SelectValue placeholder="Select condition" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select...</SelectItem>
                    {TOOL_CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fixed Asset → Calibration checkbox */}
        {!isMiscMode && isFixedAssetReceive && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Checkbox id="add-to-calibration" checked={form.add_to_calibration}
                  onCheckedChange={v => updateField('add_to_calibration', v)} data-testid="add-to-calibration-checkbox" />
                <Label htmlFor="add-to-calibration" className="text-xs font-medium cursor-pointer">
                  Add this item to Calibration List
                </Label>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 ml-6">
                If checked, this fixed asset will be registered as a calibration instrument after receipt.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Remarks & Submit for normal mode */}
        {!isMiscMode && (
          <>
            <Card>
              <CardContent className="pt-4">
                <Label className="text-xs">Remarks</Label>
                <Textarea value={form.remarks} onChange={e => updateField('remarks', e.target.value)}
                  placeholder="Additional notes..." className="text-xs min-h-[60px]" data-testid="txn-remarks" />
              </CardContent>
            </Card>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-transaction-btn">
                <Save className="w-4 h-4 mr-1" />
                {loading ? 'Saving...' : 'Record Transaction'}
              </Button>
            </div>
          </>
        )}
      </form>

      {/* Calibration Data Dialog */}
      <CalibrationDialog
        open={showCalibrationDialog}
        onOpenChange={setShowCalibrationDialog}
        calibrationData={calibrationData}
        updateCal={updateCal}
        onConfirm={() => { setShowCalibrationDialog(false); submitTransaction(true); }}
        loading={loading}
      />

      {/* Auto-suggest: offer CTS reconciliation right after receiving full sheets against a CTS PO */}
      <AlertDialog open={!!reconcilePromptLine && !reconcileOpen}>
        <AlertDialogContent data-testid="cts-reconcile-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>Reconcile to Cut-to-Size now?</AlertDialogTitle>
            <AlertDialogDescription>
              You just received <strong>{reconcilePromptLine?.pending_sheets}</strong> full sheet(s) of{' '}
              <strong>{reconcilePromptLine?.item_name}</strong> against Cut-to-Size PO{' '}
              <strong>{reconcilePromptLine?.po_number}</strong>. Convert these full sheets into the linked
              cut CTS pieces (STORE) + scrap (SCRAP yard) now, or do it later from Inventory → CTS Reconciliation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setReconcilePromptLine(null); navigate('/inventory/transactions'); }}
              data-testid="cts-reconcile-later">Later</AlertDialogCancel>
            <AlertDialogAction onClick={() => setReconcileOpen(true)}
              className="bg-amber-600 hover:bg-amber-700" data-testid="cts-reconcile-now">Reconcile now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CTSReconcileDialog
        line={reconcileOpen ? reconcilePromptLine : null}
        open={reconcileOpen}
        onClose={() => { setReconcileOpen(false); setReconcilePromptLine(null); navigate('/inventory/transactions'); }}
        onDone={() => {}}
      />
    </div>
  );
}
